import { keyframes } from "@emotion/react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faChevronRight, faEllipsis } from "@fortawesome/pro-solid-svg-icons";
import { faHouseBlank } from "@fortawesome/pro-light-svg-icons";
import { faHouseBlank as faHouseBlankRegular } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export type SpaceBreadcrumbProps = {
  /** The space's display name — the root ("home") crumb. */
  spaceName: string;
  /** Current directory as folder names from the root (root is `[]`). */
  path: string[];
  /** Navigate to a directory identified by its full path from the root. */
  onNavigate: (path: string[]) => void;
  /**
   * Space-wide select-all control shown ahead of the trail. The leading
   * checkbox only appears in selection mode (≥1 selected) — animating in and
   * sliding the trail right — and mirrors the share-link toolbar: indeterminate
   * + "Select all files in this space" while a subset is picked, checked + "Clear selection"
   * once everything is selected.
   */
  selection?: {
    selectedCount: number;
    totalCount: number;
    onSelectAll: () => void;
    onClear: () => void;
  };
};

type Crumb = { label: string; path: string[] };

/** Shared easing — matches the space viewer's other transitions (sheet, pill). */
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";
/** Ellipsis button width (px) — used when computing how many crumbs fit. */
const ELLIPSIS_W = 28;
/** Fallback separator width (px) if it can't be measured. */
const SEPARATOR_W = 32;

/** Hover fill, pressed state (deeper fill + slight push-down) and focus ring
 *  shared by every clickable breadcrumb element. */
const CRUMB_BUTTON_SX = {
  border: 0,
  background: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "background-color 0.15s ease, transform 0.12s ease-out",
  "&:hover": { backgroundColor: "rgba(17,17,26,0.05)" },
  "&:active": {
    backgroundColor: "rgba(17,17,26,0.09)",
    transform: "scale(0.97)",
  },
  "&:focus-visible": {
    outline: "2px solid var(--primary-color)",
    outlineOffset: "1px",
  },
} as const;

/** Elements are truncated when their content overflows the visible box. */
const isTruncated = (el: HTMLElement) => el.scrollWidth > el.clientWidth + 1;

/**
 * One trail crumb. Clickable crumbs are text buttons with a rounded hover fill
 * (padding cancelled by negative margins, so only the fill is bigger); the
 * current crumb is bold plain text. Both shrink with an ellipsis, and the
 * full-name tooltip only appears when the label is actually truncated.
 */
function TrailCrumb({
  crumb,
  isCurrent,
  onNavigate,
}: {
  crumb: Crumb;
  isCurrent: boolean;
  onNavigate: (path: string[]) => void;
}) {
  const [truncated, setTruncated] = useState(false);
  const measure = (e: React.MouseEvent<HTMLElement>) =>
    setTruncated(isTruncated(e.currentTarget));
  const title = truncated ? crumb.label : undefined;
  return isCurrent ? (
    <Typography
      noWrap
      title={title}
      onMouseEnter={measure}
      sx={{ fontSize: 15, fontWeight: 700, color: "#1A1A1A", minWidth: 0 }}
    >
      {crumb.label}
    </Typography>
  ) : (
    <Box
      component="button"
      type="button"
      onClick={() => onNavigate(crumb.path)}
      title={title}
      onMouseEnter={measure}
      sx={{
        ...CRUMB_BUTTON_SX,
        fontSize: 15,
        fontWeight: 400,
        color: "#1A1A1A",
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        borderRadius: "6px",
        px: "6px",
        mx: "-6px",
        py: "3px",
      }}
    >
      {crumb.label}
    </Box>
  );
}

/** Fade + slight slide played when the trail switches compact⇄expanded. */
const crumbFade = keyframes`
  from { opacity: 0; transform: translateY(1px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const SEPARATOR = (
  <FontAwesomeIcon
    icon={faChevronRight as IconProp}
    style={{ fontSize: 12, color: "#DADADA", margin: "0 10px", flex: "none" }}
  />
);

/**
 * Desktop breadcrumb trail for the space viewer.
 *
 * Renders `⌂ space › … › …trailing` where the home icon and the space name form
 * a single button (both navigate to the root). When the folder trail is wider
 * than the available track it goes **compact**: the leading folders fold into
 * an ellipsis while as many trailing folders as fit stay visible, so the trail
 * always fills the width and keeps the deepest context. As the track widens
 * more trailing folders reappear; once everything fits, the ellipsis is
 * dropped. The ellipsis opens an anchored {@link Menu} of the folded folders
 * (in order) for direct navigation. Clickable crumbs render as text buttons
 * (rounded hover fill, no underline).
 *
 * The optional leading select-all {@link Checkbox} reserves no space when idle:
 * entering selection mode animates it in and slides the trail right; the compact
 * fit re-evaluates automatically (via the track's ResizeObserver) as it does.
 */
export function SpaceBreadcrumb({
  spaceName,
  path,
  onNavigate,
  selection,
}: SpaceBreadcrumbProps) {
  const { t } = useTranslation();

  // Folder crumbs only; the root is the home button ahead of the track.
  const crumbs: Crumb[] = path.map((name, idx) => ({
    label: name,
    path: path.slice(0, idx + 1),
  }));
  const n = crumbs.length;

  const trackRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const crumbRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const sepRef = useRef<HTMLSpanElement | null>(null);
  // Number of trailing crumbs shown after the ellipsis; `null` = show them all
  // (no collapse). Always ≥ 1 when compact (the current folder stays visible).
  const [trailingCount, setTrailingCount] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  // Measured on hover; a fully visible space name shows no tooltip.
  const homeNameRef = useRef<HTMLElement>(null);
  const [homeNameTruncated, setHomeNameTruncated] = useState(false);

  // Recompute the compact fit against the available track width: fit as many
  // trailing crumbs (from the end) as possible, reserving room for the
  // ellipsis. Each crumb is preceded by a separator (the first one follows the
  // home button). Re-runs on track resize (which also covers the select-all
  // checkbox sliding in/out) and whenever the path changes.
  useLayoutEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const compute = () => {
      if (n <= 1) {
        setTrailingCount(null); // at most one folder: nothing to fold
        return;
      }
      const labelW = crumbRefs.current.map((el) => el?.offsetWidth ?? 0);
      const sepW = sepRef.current?.offsetWidth ?? SEPARATOR_W;
      const avail = track.clientWidth;
      const fullWidth = labelW.reduce((a, b) => a + b, 0) + sepW * n;
      if (fullWidth <= avail) {
        setTrailingCount(null); // everything fits
        return;
      }
      // Compact: (sep + ellipsis) + trailing crumbs that fit.
      let used = sepW + ELLIPSIS_W;
      let count = 0;
      for (let i = n - 1; i >= 0; i--) {
        const add = sepW + labelW[i];
        if (used + add <= avail) {
          used += add;
          count += 1;
        } else break;
      }
      count = Math.max(1, count); // always keep the current folder
      // If nothing actually folds away, don't show the ellipsis.
      setTrailingCount(count >= n ? null : count);
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(track);
    return () => observer.disconnect();
  }, [n, path]);

  const allSelected =
    !!selection &&
    selection.totalCount > 0 &&
    selection.selectedCount === selection.totalCount;
  const someSelected = !!selection && selection.selectedCount > 0;

  const isCompact = trailingCount != null;
  const trailing = isCompact ? crumbs.slice(n - trailingCount!) : [];
  const folded = isCompact ? crumbs.slice(0, n - trailingCount!) : [];

  const ellipsis = (
    <Tooltip title={t("Show path")} placement="top">
      <IconButton
        size="small"
        aria-label={t("Show path")}
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        sx={{
          width: ELLIPSIS_W,
          height: 24,
          borderRadius: "6px",
          flex: "none",
          color: "#7d7d87",
          "&:hover": { backgroundColor: "rgba(17,17,26,0.05)" },
        }}
      >
        {/* An icon (not the "…" text glyph, which sits on the baseline and reads
            low) so the dots are vertically centered with the crumbs. */}
        <FontAwesomeIcon
          icon={faEllipsis as IconProp}
          style={{ fontSize: 14 }}
        />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
      {selection && (
        // Zero-width when idle (no reserved space); expands + fades in and pushes
        // the trail right on entering selection mode. `overflow:hidden` clips the
        // checkbox as it collapses back.
        <Box
          sx={{
            flex: "none",
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
            width: someSelected ? 34 : 0,
            opacity: someSelected ? 1 : 0,
            transition: `width 0.32s ${EASE}, opacity 0.24s ease`,
            pointerEvents: someSelected ? "auto" : "none",
          }}
        >
          <Box
            sx={{
              transform: someSelected ? "scale(1)" : "scale(0.55)",
              transformOrigin: "center",
              transition: `transform 0.32s ${EASE}`,
            }}
          >
            <Tooltip
              title={
                allSelected
                  ? t("Clear selection")
                  : t("Select all files in this space")
              }
              placement="top"
            >
              <Checkbox
                disableRipple
                size="small"
                color="primary"
                checked={allSelected}
                indeterminate={someSelected && !allSelected}
                onChange={() =>
                  allSelected ? selection.onClear() : selection.onSelectAll()
                }
                aria-hidden={!someSelected}
                tabIndex={someSelected ? 0 : -1}
                aria-label={
                  allSelected
                    ? t("Clear selection")
                    : t("Select all files in this space")
                }
                sx={{ p: 0.5, flex: "none" }}
              />
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* Home icon + space name: one button (both navigate to the root).
          Padding/margins keep the icon and name at their previous spots. The
          tooltip only appears when the name is actually truncated. */}
      <Tooltip
        title={homeNameTruncated ? spaceName || t("Home") : ""}
        placement="top"
      >
        <Box
          component="button"
          type="button"
          onClick={() => onNavigate([])}
          onMouseEnter={() => {
            const el = homeNameRef.current;
            setHomeNameTruncated(!!el && isTruncated(el));
          }}
          sx={{
            ...CRUMB_BUTTON_SX,
            display: "flex",
            alignItems: "center",
            // Shrinkable so a long space name ellipsizes.
            flex: "0 1 auto",
            minWidth: 0,
            height: 30,
            borderRadius: "8px",
            pl: "7px",
            pr: "8px",
            mr: "-8px",
          }}
        >
          <FontAwesomeIcon
            // Thicker house while the root is open (the name goes bold too).
            icon={
              (path.length === 0
                ? faHouseBlankRegular
                : faHouseBlank) as IconProp
            }
            style={{ fontSize: 15, flex: "none", color: "#1A1A1A" }}
          />
          <Typography
            ref={homeNameRef}
            noWrap
            sx={{
              fontSize: 15,
              // The space name is the current crumb (bold) at the root.
              fontWeight: path.length === 0 ? 700 : 400,
              color: "#1A1A1A",
              minWidth: 0,
              ml: "16px",
              // Nudged down like the trail, so icon + name read on one line.
              position: "relative",
              top: "2px",
            }}
          >
            {spaceName || t("Home")}
          </Typography>
        </Box>
      </Tooltip>

      {/* Trail track (fills remaining width; clips overflow), nudged down a
          touch per design. Each crumb brings its own leading separator. */}
      <Box
        ref={trackRef}
        sx={{
          position: "relative",
          top: "2px",
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Hidden measurer: each crumb label (at its real weight) + one separator,
            read per-element to decide the compact fit. */}
        <Box
          ref={measureRef}
          aria-hidden
          sx={{
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "nowrap",
            display: "inline-flex",
            alignItems: "center",
            pointerEvents: "none",
          }}
        >
          <span
            ref={sepRef}
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            {SEPARATOR}
          </span>
          {crumbs.map((crumb, idx) => (
            <span
              key={idx}
              ref={(el) => (crumbRefs.current[idx] = el)}
              style={{
                fontSize: 15,
                fontWeight: idx === n - 1 ? 700 : 400,
                whiteSpace: "nowrap",
              }}
            >
              {crumb.label}
            </span>
          ))}
        </Box>

        {/* Visible trail. Re-keyed on compact-vs-expanded so the swap animates. */}
        <Box
          key={isCompact ? "compact" : "expanded"}
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: 0,
            overflow: "hidden",
            animation: `${crumbFade} .25s ease`,
          }}
        >
          {isCompact ? (
            <>
              {SEPARATOR}
              {ellipsis}
              {trailing.map((crumb, i) => {
                const idx = n - trailingCount! + i;
                return (
                  <Box
                    key={idx}
                    sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
                  >
                    {SEPARATOR}
                    <TrailCrumb
                      crumb={crumb}
                      isCurrent={idx === n - 1}
                      onNavigate={onNavigate}
                    />
                  </Box>
                );
              })}
            </>
          ) : (
            crumbs.map((crumb, idx) => (
              <Box
                key={idx}
                sx={{ display: "flex", alignItems: "center", minWidth: 0 }}
              >
                {SEPARATOR}
                <TrailCrumb
                  crumb={crumb}
                  isCurrent={idx === n - 1}
                  onNavigate={onNavigate}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Folded-folders menu, anchored at the ellipsis; primary-light hover. */}
      <Menu
        anchorEl={menuAnchor}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {folded.map((crumb, idx) => (
          <MenuItem
            key={idx}
            onClick={() => {
              setMenuAnchor(null);
              onNavigate(crumb.path);
            }}
            sx={{
              fontSize: 14,
              minWidth: 160,
              "&:hover": { backgroundColor: "var(--primary-light-color)" },
            }}
          >
            {crumb.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
